import { useState, type ReactNode } from 'react';
import { MICRO_USD_PER_DOLLAR, jevBudgetAmountProblem, jevBudgetHint, jevBudgetPresetLabel } from '@nest-recruit/shared';
import styles from './JevBudgetPicker.module.css';
import { digitsOf, MoneyInput } from './MoneyInput';
import { Segmented, type SegmentedOption } from './Segmented';

// ADR 0070: the Jev budget as a few presets and "Other". Used on the request (REQ-01, HM-03) and by an approver who changes the amount.
// The value is in millionths of a US dollar (the unit Jev's spend uses); "Other" is typed as whole dollars.

const OTHER = 'other';
/** Room to type a wrong amount and be told so, instead of having it quietly cut to the limit. */
const MAX_DIGITS = 7;
/** What is said while the box is empty: the same sentence an out-of-range amount gets. */
const RANGE_NOTE = jevBudgetAmountProblem(0) ?? '';

interface JevBudgetPickerProps {
  readonly label: string;
  readonly value: number | null;
  readonly presets: readonly number[];
  readonly onChange: (value: number | null) => void;
  /** The id the "Other" amount box takes, so a Field's label points at it. */
  readonly id?: string;
  /** The live line under the choice: what the amount covers. On by default. */
  readonly showHint?: boolean;
  /** What the line says while nothing is chosen. */
  readonly emptyHint?: string;
}

export function JevBudgetPicker({ label, value, presets, onChange, id, showHint = true, emptyHint = 'No Jev budget asked for.' }: JevBudgetPickerProps): ReactNode {
  const isPreset = value !== null && presets.includes(value);
  // "Other" was pressed, or the value is not one of the presets (a request saved with a typed amount).
  const [isOtherChosen, setOtherChosen] = useState(false);
  const isOther = isOtherChosen || (value !== null && !isPreset);
  // What was typed; null until the person types, so a value that arrives later fills the box.
  const [typed, setTyped] = useState<string | null>(null);
  const digits = typed ?? (value !== null && !isPreset ? String(Math.round(value / MICRO_USD_PER_DOLLAR)) : '');

  const options: readonly SegmentedOption<string>[] = [
    ...presets.map((preset) => ({ value: String(preset), label: jevBudgetPresetLabel(preset) })),
    { value: OTHER, label: 'Other' },
  ];

  function pick(next: string): void {
    if (next === OTHER) {
      setOtherChosen(true);
      onChange(amountOf(digits));
      return;
    }
    setOtherChosen(false);
    setTyped(null);
    onChange(Number(next));
  }

  function type(raw: string): void {
    const clean = digitsOf(raw, MAX_DIGITS);
    setTyped(clean);
    onChange(amountOf(clean));
  }

  // An amount below $1 or above $1,000 stays as typed and is called out; the form or the dialog holds its button off (never clamped).
  const problem = isOther ? (digits === '' ? RANGE_NOTE : jevBudgetAmountProblem(amountOf(digits))) : null;
  // "Covers roughly …" is said for a good amount or none; for a wrong one only the note speaks.
  const isAmountWrong = problem !== null && digits !== '';
  return (
    <div className={styles.picker}>
      <Segmented label={label} value={isOther ? OTHER : value === null ? '' : String(value)} options={options} onChange={pick} />
      {isOther && (
        <div className={styles.other}>
          <MoneyInput id={id} label="Jev budget in dollars" value={digits} onChange={type} currency="USD" suffix="for this job" maxDigits={MAX_DIGITS} />
          {problem && (
            <span className={styles.note} role="alert">
              {problem}
            </span>
          )}
        </div>
      )}
      {showHint && !isAmountWrong && (
        <p className={styles.hint} aria-live="polite">
          {value === null ? emptyHint : jevBudgetHint(value)}
        </p>
      )}
    </div>
  );
}

/** Whole dollars typed → millionths of a dollar, as typed (even out of range, so it can be called out); null when nothing is typed. */
function amountOf(digits: string): number | null {
  return digits === '' ? null : Number(digits) * MICRO_USD_PER_DOLLAR;
}
